import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  // The router's own location context, which is what `useLocation` reads.
  // Unsafe in the sense of unversioned, and used here for one thing only: to
  // hold a departing screen on the URL it was rendered under.
  UNSAFE_LocationContext as LocationContext,
  useOutlet,
  type Location,
  type NavigationType,
} from "react-router-dom";
import { useIsMobile } from "../ui/useIsMobile";

/**
 * How a screen arrives on a phone.
 *
 * A screen opened from another one slides in from the right and covers it,
 * the way a native app pushes a screen onto its stack; closing it slides it
 * back out to the right and uncovers the one underneath. Swapping between the
 * app's sections — one tab of the bar for another — is a swap, not a push, so
 * it is instant: two sections are side by side, and neither is above the
 * other.
 */
export type PageDirection = "forward" | "back" | "none";

/**
 * The state a navigation passes to say which way it is going, when the paths
 * alone would get it wrong. `navigate(to, { state: transitionState("back") })`.
 *
 * Needed by a screen returning to the one that opened it when the two are the
 * same distance from the root — the plan builder going back to a client's
 * Plans tab, say. Read by `pageDirection`; every other navigation leaves it
 * off and is judged by its path.
 */
export const transitionState = (direction: PageDirection) => ({
  transition: direction,
});

const isDirection = (value: unknown): value is PageDirection =>
  value === "forward" || value === "back" || value === "none";

const segments = (pathname: string) => pathname.split("/").filter(Boolean);

/**
 * Which way a change of screen goes, worked out from the paths.
 *
 * The app's paths are a tree — `/my-clients`, `/my-clients/:id`,
 * `/my-clients/:id/edit` — and a screen is opened *from* the one above it, so
 * going deeper is forward and coming back up is back. That is what decides
 * the ordinary case, and it holds however the navigation was made: nearly
 * every way out of a form here is `navigate(list)` rather than `navigate(-1)`,
 * and the browser's own Back button is the exception rather than the rule.
 *
 * Two things override the tree. The browser's Back and Forward buttons say
 * which way they went — the router numbers the history entries, and `steps`
 * is how many were crossed — and a navigation can carry `transitionState`
 * when its paths would mislead.
 *
 * Crossing into another section is a swap, not a push, when it lands on that
 * section's own screen: the tab bar and the account menu go to those, and a
 * form's "go to My brand" is the same jump. Landing deeper in another section
 * — a client's plan opened in the plan builder — is a push, since it was
 * opened from here.
 */
export function pageDirection(
  from: Location,
  to: Location,
  navigationType: NavigationType,
  steps: number | null
): PageDirection {
  // Only the query or the hash changed: a tab of the same screen, which stays
  // where it is.
  if (from.pathname === to.pathname) return "none";

  if (navigationType === "POP" && steps !== null && steps !== 0) {
    return steps < 0 ? "back" : "forward";
  }

  const asked = (to.state as { transition?: unknown } | null)?.transition;
  if (isDirection(asked)) return asked;

  const before = segments(from.pathname);
  const after = segments(to.pathname);
  if (before[0] !== after[0]) return after.length <= 1 ? "none" : "forward";
  return after.length < before.length ? "back" : "forward";
}

/**
 * Where the router has put this entry in the browser's history — it stamps
 * every entry it writes with an index, which is what lets a `POP` say whether
 * it went back or forward. Null under a memory router, and in any test, where
 * the browser's history is never written; the paths decide then.
 */
const historyIndex = (): number | null => {
  if (typeof window === "undefined") return null;
  const index = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof index === "number" ? index : null;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * How long the slide is given before the screen being left is dropped
 * whatever happened — a little longer than the animation in `index.css`, for
 * the case where the stylesheet did not run it at all (the width crossing
 * `md` mid-flight, say) and there is no `animationend` to wait for.
 */
export const SETTLE_FALLBACK_MS = 500;

/** The screen on show: which mounting it is, and the path it was opened on. */
interface Shown {
  id: number;
  pathname: string;
}

/**
 * The screen being left, kept mounted until it has slid away. It holds the
 * element the outlet rendered for it and the location it rendered under, so
 * it is drawn exactly as it was for the length of the slide.
 */
interface Leaving {
  id: number;
  element: React.ReactElement | null;
  location: Location;
  navigationType: NavigationType;
  direction: Exclude<PageDirection, "none">;
}

/**
 * The routed screen, and the slide between two of them on a phone.
 *
 * Stands where the shell's `<Outlet />` was. From `md` up, and for a reader
 * who has asked for less motion, it *is* the outlet: one screen in one
 * scrolling panel, replaced in place the moment the route changes, and no
 * different from before. Below `md` a change of screen keeps the old one
 * mounted for a third of a second while the new one slides in over it (or,
 * going back, while the old one slides off and uncovers the new). Which of
 * the two is on top, and which edge they move to, is `pageDirection`'s call.
 *
 * The screen being left is drawn from the element the outlet rendered for it
 * on its last render, under the location it had then — its `useLocation`
 * and `useSearchParams` keep answering with the URL it was opened on, so a
 * Settings page sliding out is still on the tab it was on. It is inert for
 * the duration: nothing on it can be pressed or focused, and it is not read
 * out.
 *
 * Every screen has its own scrolling panel rather than sharing one, which is
 * what a slide needs — the one leaving keeps its place while the one arriving
 * starts at its top — and is what the shell should have had anyway: a
 * shared scroller carried the list's scroll position into the detail screen
 * it opened.
 *
 * Whether a route change is a slide is decided during render, not in an
 * effect, on purpose: an effect would run one frame after the outlet had
 * already swapped the old screen's component for the new one inside the same
 * panel, and putting the old one back would be a remount — a skeleton where
 * the screen was.
 */
interface PageTransitionProps {
  /**
   * Handed the panel on show — the element that scrolls — and null between
   * screens. The shell reads the pull-to-refresh gesture from it; see
   * `PullFrame`. A callback rather than a ref object because the panel is
   * re-keyed for every screen, and whoever holds it has to be told.
   */
  panelRef?: (panel: HTMLDivElement | null) => void;
  /**
   * Whether the screen this mounts on should arrive rather than be there
   * already — the one push this component cannot work out for itself.
   *
   * Nearly every screen change happens under a shell that is already standing,
   * and the slide is from the screen it was holding a moment ago. The paid step
   * of a new practice's way in is the exception: it is opened from the trial
   * offer, which is outside `AppLayout`, so the shell mounts *with* it and the
   * screen it was opened from is not this component's to animate away. Without
   * this it would simply appear, which is the one hop of that flow that would
   * read as a page load. See `AppLayout`.
   */
  enterOnMount?: boolean;
}

const PageTransition: React.FC<PageTransitionProps> = ({
  panelRef,
  enterOnMount,
}) => {
  const outlet = useOutlet();
  const locationContext = useContext(LocationContext);
  const { location, navigationType } = locationContext;
  const isMobile = useIsMobile();

  const [shown, setShown] = useState<Shown>({
    id: 0,
    pathname: location.pathname,
  });
  const [leaving, setLeaving] = useState<Leaving | null>(null);

  // The mount push, decided once and spent when it finishes — on the same
  // terms as every other slide here: phones only, and nothing for a reader
  // who has asked for less motion. Held in state rather than read per render
  // so the class comes off when the animation ends and the panel is an
  // ordinary one from then on.
  const [arriving, setArriving] = useState(
    () => !!enterOnMount && isMobile && !prefersReducedMotion()
  );

  // What the outlet rendered last time round, and where the history was —
  // the previous screen, which is what a slide is away from. Written after
  // each commit, so on the render that first sees a new path it still
  // describes the old one.
  const last = useRef({
    outlet,
    location,
    navigationType,
    index: historyIndex(),
  });
  useLayoutEffect(() => {
    last.current = { outlet, location, navigationType, index: historyIndex() };
  });

  if (shown.pathname !== location.pathname) {
    // The mount push is over the moment there is a second screen, whether or
    // not its `animationend` ever arrived — the stylesheet may not have run it
    // at all, the way the width crossing `md` mid-flight can leave a slide
    // unfinished. From here the ordinary rules apply.
    if (arriving) setArriving(false);

    const previous = last.current;
    const index = historyIndex();
    const steps =
      index !== null && previous.index !== null ? index - previous.index : null;
    const direction =
      isMobile && !prefersReducedMotion()
        ? pageDirection(previous.location, location, navigationType, steps)
        : "none";

    if (direction === "none") {
      // Same panel, new screen: the outlet swaps them in place, as it always
      // did. A slide still under way is cut short rather than finished.
      setShown({ id: shown.id, pathname: location.pathname });
      setLeaving(null);
    } else {
      // The panel on show becomes the one leaving — same key, so its screen
      // stays mounted rather than being drawn again — and a new panel takes
      // the new screen.
      setShown({ id: shown.id + 1, pathname: location.pathname });
      setLeaving({
        id: shown.id,
        element: previous.outlet,
        location: previous.location,
        navigationType: previous.navigationType,
        direction,
      });
    }
  }

  // Drops the departed screen, but only the one asked about: a second
  // navigation during the slide has already replaced it.
  const settle = useCallback((id: number) => {
    setLeaving((current) => (current?.id === id ? null : current));
  }, []);

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => settle(leaving.id), SETTLE_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [leaving, settle]);

  // `overscroll-contain` keeps a pull at the top of the panel from reaching
  // the browser, whose own pull-to-refresh on Android reloads the page.
  // The fill is the shell's to name — `--page-fill` — because a step of a new
  // practice's way in is painted white rather than on the app's off-white, and
  // a panel sliding in has to be the same colour as the one it covers. The
  // literal stands in wherever this is mounted outside that shell.
  const panel =
    "page-panel absolute inset-0 overflow-y-auto overscroll-contain custom-scrollbar-overlay bg-[color:var(--page-fill,#FCFCFD)]";

  // The departing screen is held still while it slides away: it is `inert`,
  // nobody can reach it, and it is on its way off the screen — so it has no
  // business being a scroller. Left as one it keeps a live scrollbar down the
  // right of the window over the top of the screen arriving, and a screen
  // captured at one width can be far taller than the panel it is leaving
  // through: a page that owns the viewport height from `lg` up does not at
  // phone width, so a slide that starts on a phone leaves a panel with a
  // screenful of overflow in it. The scrollbar that draws is the only part of
  // it anybody sees, and it belongs to a screen that is no longer there.
  const leavingPanel = panel.replace("overflow-y-auto", "overflow-hidden");

  return (
    // The stage the panels slide across. It takes the column's remaining
    // height the way the single scroller did, and clips whatever is part-way
    // off either edge.
    <div className="relative flex-1 min-h-0 overflow-hidden">
      {leaving && (
        <div
          key={leaving.id}
          className={`${leavingPanel} page-panel-leave-${leaving.direction}`}
          inert
          aria-hidden="true"
          onAnimationEnd={(event) => {
            // The panel's own slide, not an animation that ended somewhere
            // inside the screen it is carrying.
            if (event.target === event.currentTarget) settle(leaving.id);
          }}
        >
          <LocationContext.Provider
            value={{
              location: leaving.location,
              navigationType: leaving.navigationType,
            }}
          >
            {leaving.element}
          </LocationContext.Provider>
        </div>
      )}
      <div
        key={shown.id}
        ref={panelRef}
        className={`${panel}${
          leaving
            ? ` page-panel-enter-${leaving.direction}`
            : arriving
            ? " page-panel-enter-forward"
            : ""
        }`}
        onAnimationEnd={(event) => {
          // The panel's own push, not an animation that ended somewhere inside
          // the screen it is carrying — and only ever the one on mount, since
          // the class is off by then for every other case.
          if (event.target === event.currentTarget) setArriving(false);
        }}
      >
        {/* The live location, passed straight through: the provider changes
            nothing for the screen on show, and is there so this panel has
            the same shape as the leaving one above — which it becomes, under
            the same key, when the next screen opens over it. */}
        <LocationContext.Provider value={locationContext}>
          {outlet}
        </LocationContext.Provider>
      </div>
    </div>
  );
};

export default PageTransition;
